import { useForm } from '@mantine/form';
import { mergeWith } from 'lodash';

import { Transit } from '@e-origin/shared';

import { useUpdateTransit } from '@hooks/transit';
import { TransitViewStakeholdersFormValues } from './validateTransitViewStakeholdersForm';

export const useTransitViewStakeholdersFormInitializer = ({ transit, openOverwriteMessage }) => {
  const updateTransitMutation = useUpdateTransit();

  const buildFormValues = (t) => ({
    TraderAtDestination: { identificationNumber: t.TraderAtDestination?.identificationNumber || '' },
    generalInfo: {
      group: t.generalInfo.group || '',
    },
    HolderOfTheTransitProcedure: {
      identificationNumber: t.HolderOfTheTransitProcedure?.identificationNumber || '',
      name: t.HolderOfTheTransitProcedure?.name || '',
      Address: {
        streetAndNumber: t.HolderOfTheTransitProcedure?.Address?.streetAndNumber || '',
        postcode: t.HolderOfTheTransitProcedure?.Address?.postcode || '',
        city: t.HolderOfTheTransitProcedure?.Address?.city || '',
        country: t.HolderOfTheTransitProcedure?.Address?.country || '',
      },
      ContactPerson: {
        name: t.HolderOfTheTransitProcedure?.ContactPerson?.name || '',
        phoneNumber: t.HolderOfTheTransitProcedure?.ContactPerson?.phoneNumber || '',
        eMailAddress: t.HolderOfTheTransitProcedure?.ContactPerson?.eMailAddress || '',
      },
    },
    Representative: {
      identificationNumber: t.Representative?.identificationNumber || '',
      status: t.Representative?.status || '',
      ContactPerson: {
        name: t.Representative?.ContactPerson?.name || '',
        phoneNumber: t.Representative?.ContactPerson?.phoneNumber || '',
        eMailAddress: t.Representative?.ContactPerson?.eMailAddress || '',
      },
    },
    Consignment: {
      Carrier: {
        identificationNumber: t.Consignment?.Carrier?.identificationNumber || '',
        ContactPerson: {
          name: t.Consignment?.Carrier?.ContactPerson?.name || '',
          phoneNumber: t.Consignment?.Carrier?.ContactPerson?.phoneNumber || '',
          eMailAddress: t.Consignment?.Carrier?.ContactPerson?.eMailAddress || '',
        },
      },
      Consignor: {
        identificationNumber: t.Consignment?.Consignor?.identificationNumber || '',
        name: t.Consignment?.Consignor?.name || '',
        Address: {
          streetAndNumber: t.Consignment?.Consignor?.Address?.streetAndNumber || '',
          postcode: t.Consignment?.Consignor?.Address?.postcode || '',
          city: t.Consignment?.Consignor?.Address?.city || '',
          country: t.Consignment?.Consignor?.Address?.country || '',
        },
        ContactPerson: {
          name: t.Consignment?.Consignor?.ContactPerson?.name || '',
          phoneNumber: t.Consignment?.Consignor?.ContactPerson?.phoneNumber || '',
          eMailAddress: t.Consignment?.Consignor?.ContactPerson?.eMailAddress || '',
        },
      },
      Consignee: {
        identificationNumber: t.Consignment?.Consignee?.identificationNumber || '',
        name: t.Consignment?.Consignee?.name || '',
        Address: {
          streetAndNumber: t.Consignment?.Consignee?.Address?.streetAndNumber || '',
          postcode: t.Consignment?.Consignee?.Address?.postcode || '',
          city: t.Consignment?.Consignee?.Address?.city || '',
          country: t.Consignment?.Consignee?.Address?.country || '',
        },
      },
      AdditionalSupplyChainActor: (t.Consignment?.AdditionalSupplyChainActor ?? []).map((additionalSupplyChainActor) => ({
        role: additionalSupplyChainActor.role || '',
        identificationNumber: additionalSupplyChainActor.identificationNumber || '',
      })),
    },
  });

  const form = useForm<TransitViewStakeholdersFormValues>({
    initialValues: buildFormValues(transit),
    validateInputOnBlur: true,
  });

  const isDataOverwritten = (data: Partial<Transit>) => {
    if (
      transit.Consignment?.HouseConsignment?.some((house) => {
        return (
          (data.Consignment?.Consignor?.name && (house.Consignor?.name || house.Consignor?.identificationNumber)) ||
          (data.Consignment?.Consignor?.identificationNumber && (house.Consignor?.name || house.Consignor?.identificationNumber)) ||
          (data.Consignment?.Consignee?.name && (house.Consignee?.name || house.Consignee?.identificationNumber)) ||
          (data.Consignment?.Consignee?.identificationNumber && (house.Consignee?.name || house.Consignee?.identificationNumber))
        );
      })
    ) {
      return true;
    }

    return false;
  };

  const onSubmit = async (values, isOverwriteConfirm) => {
    if (!isOverwriteConfirm && isDataOverwritten(values)) {
      openOverwriteMessage();
      return;
    }

    updateTransitMutation.mutate({
      transit: mergeWith({ _id: transit._id }, values, (objValue, srcValue) => (Array.isArray(srcValue) ? srcValue : undefined)),
    });

    form.resetDirty({});
  };

  return { form, onSubmit, buildFormValues };
};
